/** Pipeline — contacts by stage, deliverability health, contact drill-in. */
import { useState } from "react";
import { Bars, Chip, Empty, ErrorNote, Panel, Readout, statusTone, useApi } from "../components/ui.js";
import { fmtDate, fmtMonthDay, int, n, pct } from "../lib/format.js";
import type { ContactRow, DeliverabilityResponse, EnrollmentRow, PipelineRow, SendRow } from "../lib/types.js";

const STAGES = ["prospect", "outreach", "replied", "installed", "trial", "paying", "churned", "disqualified", "unsubscribed"];

export function Pipeline() {
  const summary = useApi<{ summary: PipelineRow[] }>("/api/pipeline/summary");
  const deliv = useApi<DeliverabilityResponse>("/api/deliverability");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const contacts = useApi<{ contacts: ContactRow[] }>(`/api/contacts?limit=50${statusFilter ? `&status=${statusFilter}` : ""}`);
  const [openId, setOpenId] = useState<string | null>(null);
  const detail = useApi<{ contact: ContactRow; sends: SendRow[]; enrollments: EnrollmentRow[] }>(openId ? `/api/contacts/${openId}` : null);

  const byStatus = new Map((summary.data?.summary ?? []).map((r) => [r.status, r]));
  const days = (deliv.data?.days ?? []).slice().reverse();
  // "Today" tiles read the API's zero-filled `today`, not days.at(-1) (the last
  // send-day — stale on any day with no sends).
  const today = deliv.data?.today ?? null;
  const sentToday = n(today?.sent);

  return (
    <>
      <div className="page-head"><h1>Pipeline</h1><span className="stamp">contacts → install → revenue</span></div>

      {/* Stage strip */}
      <Panel title="Contacts by stage" delay={0} className="">
        {summary.error ? <ErrorNote error={summary.error} /> : (
          <div className="grid cols-4" style={{ gridTemplateColumns: "repeat(9, 1fr)" }}>
            {STAGES.map((s) => {
              const r = byStatus.get(s);
              return (
                <div key={s} className="clickable" onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
                     style={statusFilter === s ? { outline: "1px solid var(--red-dim)", padding: 4 } : { padding: 4 }}>
                  <Readout label={s} value={int(r?.contacts ?? 0)} tone={s === "paying" ? "green" : undefined}
                           note={r && n(r.in_active_sequence) > 0 ? `${int(r.in_active_sequence)} in sequence` : undefined} />
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      <div className="grid cols-2" style={{ marginTop: 14 }}>
        <Panel title="Deliverability — sender health" delay={60}>
          {deliv.error ? <ErrorNote error={deliv.error} /> : (
            <>
              <Bars values={days.map((d) => n(d.sent))} labels={days.map((d) => fmtMonthDay(d.day))} color="var(--green)" />
              <hr className="rule" />
              <div className="grid cols-3">
                <Readout label="Sent today" value={int(sentToday)} note={deliv.data?.dailySendCap ? `cap ${deliv.data.dailySendCap}/day` : undefined} />
                <Readout label="Bounce today" value={sentToday > 0 ? pct(today?.bounce_rate) : "—"} tone={sentToday > 0 && n(today?.bounce_rate) > 0.05 ? "red" : "green"} />
                <Readout label="Unsub today" value={sentToday > 0 ? pct(today?.unsubscribe_rate) : "—"} />
              </div>
            </>
          )}
        </Panel>

        <Panel title={openId ? `contact #${openId}` : "contact detail"} delay={120}>
          {!openId ? <Empty>select a contact</Empty> : detail.error ? <ErrorNote error={detail.error} /> : detail.data ? (
            <>
              <div className="row">
                <strong>{detail.data.contact.full_name ?? detail.data.contact.email}</strong>
                <Chip tone={statusTone(detail.data.contact.status)}>{detail.data.contact.status}</Chip>
                {detail.data.contact.do_not_contact ? <Chip tone="red">do-not-contact</Chip> : null}
              </div>
              <div className="dim" style={{ fontSize: 14, margin: "4px 0 10px" }}>
                {detail.data.contact.title ?? "—"} · {detail.data.contact.company ?? "—"} · {detail.data.contact.email}
              </div>
              {detail.data.enrollments.length > 0 ? (
                <div className="tag" style={{ marginBottom: 8 }}>
                  {detail.data.enrollments[0]?.sequence_name ?? "sequence"} · step {detail.data.enrollments[0]?.current_step}
                  {detail.data.enrollments[0]?.is_active ? " · active" : ` · exited (${detail.data.enrollments[0]?.exit_reason ?? "done"})`}
                </div>
              ) : null}
              <table className="data">
                <thead><tr><th>step</th><th>subject</th><th>status</th><th>sent</th></tr></thead>
                <tbody>
                  {detail.data.sends.map((s) => (
                    <tr key={s.id}>
                      <td className="num">{s.step_number ?? "—"}</td>
                      <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.subject}</td>
                      <td><Chip tone={statusTone(s.status)}>{s.status}</Chip></td>
                      <td className="num dim">{fmtDate(s.sent_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : <Empty>loading…</Empty>}
        </Panel>
      </div>

      <Panel title={`Contacts ${statusFilter ? `· ${statusFilter}` : ""}`} delay={180} className="" >
        {contacts.error ? <ErrorNote error={contacts.error} /> : (contacts.data?.contacts ?? []).length === 0 ? <Empty>no contacts match</Empty> : (
          <table className="data">
            <thead><tr><th>name</th><th>title</th><th>company</th><th>status</th><th>updated</th></tr></thead>
            <tbody>
              {(contacts.data?.contacts ?? []).map((c) => (
                <tr key={c.id} className="clickable" onClick={() => setOpenId(c.id)}>
                  <td>{c.full_name ?? c.email}</td>
                  <td className="dim">{c.title ?? "—"}</td>
                  <td className="dim">{c.company ?? "—"}</td>
                  <td><Chip tone={statusTone(c.status)}>{c.status}</Chip></td>
                  <td className="num dim">{fmtDate(c.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}
